import React, { useEffect } from 'react';
import styled from 'styled-components';
import { MovieType } from '../../../types/entities';
import ShowtimesBlockContent from './ShowtimesBlockContent';
import ShowtimesBlockCinemaFilters from './ShowtimesBlockCinemaFilters';
import ShowtimesAreaSelector from './ShowtimesAreaSelector';
import { calculateDistanceInKm, PositionType } from '../../../utils/location';

const Container = styled.div`
    border-bottom: 1px solid var(--text-primary);
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;

    &:last-child {
        border-bottom: 0;
    }
`;

const Heading = styled.div`
    margin-bottom: 1.5rem;
`;

const TitleBlock = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Title = styled.h3`
    font-family: var(--ff-title);
    font-size: 1.75rem;
    margin-bottom: 0;
    text-transform: uppercase;
`;

const Subtitle = styled.p`
    margin: 0.5rem 0 0;
`;

const SearchBox = styled.div`
    border: 1px solid var(--text-primary);
    margin-bottom: 2.5rem;
    padding: 0.25rem 2.5rem;
    position: relative;
`;

const SearchIcon = styled.em`
    left: 0.55rem;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

const SearchCloseIcon = styled.em`
    cursor: pointer;
    position: absolute;
    right: 0.55rem;
    top: 50%;
    transform: translateY(-50%);
`;

const SearchInput = styled.input`
    border: 0;
    font-size: 0.82rem;
    outline: none;
    padding: 0;
    width: 100%;
`;

export interface ShowtimesBlockProps {
    loading: boolean;
    emptyMessage?: string;
    data?: any;
    date?: string;
    movie: MovieType;
    attributes?: { [key: string]: { display_name: string } };
    formats?: Array<string>;
    languages?: Array<string>;
    filters: {
        format: Array<string>,
        lang: Array<string>,
    };
    title?: string;
    subtitle?: string | React.ReactNode;
    favCinemasIds?: Array<number>;
    onCinemaFave: (cinemaId: number, isFave: boolean) => void;
    Content?: (props) => React.ReactNode;
    cinemaFiltersEnabled?: boolean;
    userLocation?: PositionType | null;
}

const ShowtimesBlock = (props: ShowtimesBlockProps) => {
    const [data, setData] = React.useState(props.data);
    const [showSearch, setShowSearch] = React.useState(false);
    const [showCinemaFilter, setShowCinemaFilter] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [cinemaConcept, setCinemaConcept] = React.useState<'traditional' | 'platinum' | 'market' | 'all'>('all');
    const [sortedBy, setSortedBy] = React.useState<'distance' | 'name' | null>(null);

    useEffect(() => {
        if (!props.data) {
            setData(undefined);
            return;
        }

        let data = props.data.cinemas

        if (sortedBy === 'distance' && props.userLocation) {
            data = data && data.sort((a, b) => {
                if (!props.userLocation) return 0;
                const distanceA = calculateDistanceInKm(props.userLocation, a.cinema.location);
                const distanceB = calculateDistanceInKm(props.userLocation, b.cinema.location);
                return distanceA - distanceB;
            });
        } else {
            data = data && data.sort((a, b) => a.cinema.name.localeCompare(b.cinema.name));
        }

        if (searchTerm) {
            data = data.filter((item) => item.cinema.name.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        if (cinemaConcept !== 'all') {
            data = data.filter((item) => {
                if ('platinum' === cinemaConcept && item.cinema.platinum) return true;
                if ('market' === cinemaConcept && item.cinema.market) return true;
                if ('traditional' === cinemaConcept && !item.cinema.platinum && !item.cinema.market) return true;
                return false;
            });
        }

        setData({ cinemas: data });
    }, [props.data, searchTerm, cinemaConcept, sortedBy]);

    function onCinemaFilterChange(options: { cinemaConcept: 'traditional' | 'platinum' | 'market' | 'all' }) {
        setCinemaConcept(options.cinemaConcept);
    }

    return <Container className="container">
        <div>
            <Heading>
                <TitleBlock>
                    <Title>{props.title}</Title>
                    {props.cinemaFiltersEnabled && <ShowtimesBlockCinemaFilters
                        setShowSearch={setShowSearch} showSearch={showSearch}
                        setShowCinemaFilter={setShowCinemaFilter}
                        showSort={props.userLocation !== null}
                        sortedBy={sortedBy}
                        setSortedBy={setSortedBy}
                    />}
                </TitleBlock>

                {props.subtitle && <Subtitle>{props.subtitle}</Subtitle>}
            </Heading>

            {showSearch && <SearchBox>
                <SearchIcon className='fal fa-search'></SearchIcon>
                <SearchInput type="text"
                    placeholder="Escribe aquí el nombre del cine"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SearchCloseIcon className='fal fa-xmark' onClick={() => setSearchTerm('')}></SearchCloseIcon>
            </SearchBox>}
        </div>

        <>{(props.Content && props.Content(props)) || <ShowtimesBlockContent {...props} data={data ? data : undefined} />}</>

        {showCinemaFilter && <ShowtimesAreaSelector
            onClose={() => setShowCinemaFilter(false)}
            onSave={onCinemaFilterChange}
            cinemaConcept={cinemaConcept}
        />}
    </Container>;
};

export default ShowtimesBlock;